import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface ResultProps {
  status?: "success" | "error" | "info" | "warning" | "404" | "403" | "500";
  title?: ReactNode;
  subTitle?: ReactNode;
  extra?: ReactNode;
  icon?: ReactNode;
  className?: string;
  children?: ReactNode;
}

// Status hues come from the --az-* semantic tokens, not raw Tailwind palettes.
const VISUAL: Record<NonNullable<ResultProps["status"]>, { icon: typeof Info; color: string }> = {
  success: { icon: CircleCheck, color: "text-[var(--az-success)]" },
  error: { icon: CircleX, color: "text-destructive" },
  info: { icon: Info, color: "text-[var(--az-info)]" },
  warning: { icon: TriangleAlert, color: "text-[var(--az-warning-accent)]" },
  "404": { icon: CircleAlert, color: "text-muted-foreground" },
  "403": { icon: CircleAlert, color: "text-muted-foreground" },
  "500": { icon: CircleAlert, color: "text-destructive" },
};

export function Result({ status = "info", title, subTitle, extra, icon, className, children }: ResultProps) {
  const v = VISUAL[status];
  const StatusIcon = v.icon;
  return (
    <div className={cn("flex flex-col items-center gap-3 py-10 text-center", className)}>
      {icon ?? <StatusIcon className={cn("size-14", v.color)} strokeWidth={1.5} />}
      {title != null && <div className="text-xl font-semibold text-foreground">{title}</div>}
      {subTitle != null && <div className="max-w-md text-sm text-muted-foreground">{subTitle}</div>}
      {extra != null && <div className="mt-2 flex flex-wrap justify-center gap-2">{extra}</div>}
      {children}
    </div>
  );
}
